import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { checkAnswer, visibleQuestions } from "@/lib/checkins";
import { PublicLoading, PublicMessage, PublicShell } from "@/components/checkins/public/PublicShell";
import { QuestionStep } from "@/components/checkins/public/QuestionStep";
import { ReviewStep } from "@/components/checkins/public/ReviewStep";
import { DoneStep, IntroStep } from "@/components/checkins/public/IntroDone";
import { DiaryView } from "@/components/checkins/public/diary/DiaryView";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/checkins`;
const detail = (e) => e?.response?.data?.detail;

function resumeIndex(template, answers) {
  const vis = visibleQuestions(template, answers);
  const i = vis.findIndex((q) => answers[q.code] === undefined || answers[q.code] === null);
  return i < 0 ? "review" : i;
}

function useAutosave(adapter, answers, enabled) {
  const [savedAt, setSavedAt] = useState(null);
  const first = useRef(true);
  useEffect(() => {
    if (!enabled || !adapter.saveDraft) return undefined;
    if (first.current) { first.current = false; return undefined; }
    const t = setTimeout(() => { adapter.saveDraft(answers).then(setSavedAt).catch(() => {}); }, 700);
    return () => clearTimeout(t);
  }, [adapter, answers, enabled]);
  return savedAt;
}

const tokenAdapter = (token) => ({
  load: () => axios.get(`${API}/${token}`).then((r) => r.data),
  saveDraft: (answers) => axios.put(`${API}/${token}/draft`, { answers }).then((r) => r.data.saved_at),
  submit: (answers) => axios.post(`${API}/${token}/submit`, { answers }).then((r) => r.data),
  setReminders: (off) => axios.post(`${API}/${token}/reminders`, { off }).then((r) => r.data.reminders_off),
});

// `adapter` / `banner` / `doneExtra` let staff preview the same flow without a client token.
export default function PublicCheckin({ adapter: custom, banner = null, doneExtra = null }) {
  const { token } = useParams();
  const adapter = useMemo(() => custom || tokenAdapter(token), [custom, token]);
  const [data, setData] = useState(null);
  const [fatal, setFatal] = useState(null);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState("intro");
  const [error, setError] = useState(null);
  const [confirmed, setConfirmed] = useState({});
  const [confirmHigh, setConfirmHigh] = useState(false);
  const [serverErrors, setServerErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    adapter.load()
      .then((d) => { setData(d); setAnswers(d.answers || {}); })
      .catch((e) => setFatal(detail(e) || "We couldn't load this progress review. Please try the link again."));
  }, [adapter]);

  const open = data?.state === "open" && data?.kind !== "diary" && !result;
  const savedAt = useAutosave(adapter, answers, open);
  const visible = useMemo(() => (open ? visibleQuestions(data.template, answers) : []), [open, data, answers]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    const q = typeof step === "number" ? visible[step] : null;
    if (q) document.getElementById(`checkin-q-${q.code}`)?.focus?.();
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  const go = useCallback((s) => { setError(null); setConfirmHigh(false); setStep(s); }, []);

  if (fatal) return <PublicShell testId="public-checkin-page" banner={banner}><PublicMessage title="This progress review isn't available" testId="checkin-unavailable"><p>{fatal}</p></PublicMessage></PublicShell>;
  if (!data) return <PublicLoading />;
  if (data.kind === "diary") {
    return (
      <PublicShell clinic={data.clinic} testId="public-checkin-page" banner={banner}>
        {data.state === "submitted" ? (
          <PublicMessage title="Your diary has been sent" testId="diary-already-submitted">
            <p>Thanks — this diary has already been sent to your practitioner. They&apos;ll go through it with you at your next appointment.</p>
          </PublicMessage>
        ) : <DiaryView data={data} adapter={adapter} />}
      </PublicShell>
    );
  }
  if (data.state === "submitted" && !result) {
    return (
      <PublicShell clinic={data.clinic} testId="public-checkin-page">
        <PublicMessage title="Already sent" testId="checkin-already-submitted">
          <p>Thanks — this progress review has already been submitted. Your exercise team will be in touch with your summary.</p>
        </PublicMessage>
      </PublicShell>
    );
  }

  const q = typeof step === "number" ? visible[Math.min(step, visible.length - 1)] : null;
  const idx = q ? visible.indexOf(q) : -1;

  function next() {
    const v = answers[q.code];
    const err = checkAnswer(q, v);
    if (err) { setError(err); return; }
    if (q.confirm_above && typeof v === "number" && v > q.confirm_above && !confirmed[q.code]) {
      setConfirmed((c) => ({ ...c, [q.code]: true }));
      setConfirmHigh(true);
      return;
    }
    go(idx + 1 >= visible.length ? "review" : idx + 1);
  }

  async function submit() {
    setSubmitting(true);
    setServerErrors([]);
    try {
      setResult(await adapter.submit(answers));
      setStep("done");
    } catch (e) {
      const d = detail(e);
      setServerErrors(d?.errors || [{ code: null, message: typeof d === "string" ? d : "Something went wrong — please try again." }]);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <PublicShell clinic={data.clinic} testId="public-checkin-page" banner={banner}>
      {step === "intro" && <IntroStep data={data} resuming={Object.keys(answers).length > 0} onStart={() => go(resumeIndex(data.template, answers))} />}
      {q && (
        <QuestionStep
          template={data.template} q={q} value={answers[q.code]} error={error} confirmHigh={confirmHigh}
          onChange={(v) => { setError(null); setConfirmHigh(false); setConfirmed((c) => ({ ...c, [q.code]: false })); setAnswers((a) => ({ ...a, [q.code]: v })); }}
          onBack={() => go(idx === 0 ? "intro" : idx - 1)} onNext={next}
          nextLabel={idx + 1 >= visible.length ? "Review answers" : "Next"}
        />
      )}
      {step === "review" && (
        <ReviewStep
          template={data.template} visible={visible} answers={answers} errors={serverErrors} submitting={submitting}
          onEdit={(code) => go(visible.findIndex((x) => x.code === code))} onSubmit={submit}
        />
      )}
      {step === "done" && result && <DoneStep result={result} />}
      {step === "done" && result && doneExtra}
      {open && step !== "intro" && savedAt && (
        <p className="mt-6 text-sm text-zinc-500" aria-live="polite" data-testid="checkin-saved-indicator">Saved automatically</p>
      )}
    </PublicShell>
  );
}
